import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Check, EyeOff, Inbox, Info, Loader2, Pencil, Tags, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '@/hooks/useApi';
import { useMe } from '@/hooks/useMe';
import { useUrlState } from '@/hooks/useUrlState';
import { useColumnPreferences, type ColumnDef } from '@/hooks/useColumnPreferences';
import { ColumnPicker } from '@/components/ui/column-picker';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { MessagedCreatorRow } from '@/components/discovery-queue/MessagedCreatorRow';
import { LogReplyDialog } from '@/components/discovery-queue/dialogs';
import { QUEUE_LEGEND, handleKey, useQueueLeads } from '@/hooks/useDiscoveryQueue';
import { InviteStagePill } from '@/components/invites/InviteStagePill';
import { Tag, channelToneFor } from '@/components/ui/tag';
import { PlatformIcon } from '@/components/PlatformIcon';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { NichesPicker } from '@/components/NichesPicker';
import { ScreenshotViewer } from '@/components/ScreenshotViewer';
import { parseSocialHandle, type SocialChannel } from '@/lib/socialHandle';
import { creatorTypeLabel, titleCase } from '@/lib/labels';
import { formatDate } from '@/lib/dates';
import { formatMoney } from '@/lib/money';
import {
  HANDLE_RECHECK,
  INVALID_HANDLE,
  MY_CREATORS_TABS,
  channelLabel,
  inviteSourceLabel,
  resolveMyCreatorsTab,
  stageAge,
  stillToBePaid,
} from '@/lib/outreach-copy';
import type { ContactedNotReplied, DiscoveryProspect, Me, OutreachInvite, OutreachNiche } from '@/api/types';

/**
 * Channels we can build a canonical handle for via parseSocialHandle. Discord
 * has no equivalent (needs guild+member resolution), so inline edit is hidden
 * for it. The "Edit details" dialog is available on all channels because
 * niches/notes/email don't depend on platform parsing.
 */
const HANDLE_EDITABLE_CHANNELS = new Set<string>(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'TWITTER']);

const PAGE_SIZE = 20;

const INVITER_COLUMNS: ColumnDef[] = [
  { key: 'target', label: 'Creator', locked: true },
  { key: 'source', label: 'Source', defaultVisible: true },
  { key: 'channel', label: 'Channel', defaultVisible: true },
  { key: 'date', label: 'Logged', defaultVisible: false },
  { key: 'category', label: 'Creator type', defaultVisible: false },
  { key: 'niche', label: 'Niche', defaultVisible: false },
  { key: 'status', label: 'Stage', defaultVisible: true },
  { key: 'age', label: 'Time in stage', defaultVisible: true },
  { key: 'followers', label: 'Followers', defaultVisible: false },
  { key: 'posts', label: 'Posts', defaultVisible: false },
  { key: 'owed', label: 'Still to be paid', defaultVisible: true },
];

const EMPTY_HINT: Record<string, string> = {
  all: 'Nothing logged yet. When a creator replies asking how to join, use Log reply.',
  replied: 'No creators waiting to join. Log a reply when someone asks how to join.',
  joined: 'None of your creators have joined yet.',
  posted: 'None of your creators have posted an approved clip yet.',
  notpaid: 'No invites were turned down. Good.',
};

export function InvitesPage(): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const [url, setUrl] = useUrlState({
    tab: 'all' as string,
    owed: false as boolean,
    page: 1,
    hideExpired: false as boolean,
  });
  const tab = resolveMyCreatorsTab(url.tab);
  const tabDef = MY_CREATORS_TABS.find((t) => t.key === tab);
  const isMessaged = tab === 'messaged';
  // Nothing is ever still to be paid on a "Not paid" invite, so the filter
  // would only ever show an empty list there.
  const owedFilterAvailable = !isMessaged && tab !== 'notpaid';
  const showOwed = owedFilterAvailable && url.owed;
  const page = Math.max(1, url.page);
  const expiredToggleAvailable = tab === 'replied' || tab === 'all';
  const hideExpired = expiredToggleAvailable && url.hideExpired;

  // v2: new Source / Time in stage columns. A fresh key means people who saved
  // column choices before still see the new defaults.
  const cols = useColumnPreferences('inviter-invites-v2', INVITER_COLUMNS);

  const me = useMe();
  const outreacher = asOutreacher(me.data);
  const teamTracks = outreacher?.teamSettings.clippingResolveRequired;
  const multipleNichesAllowed = outreacher ? outreacher.teamSettings.multipleNichesAllowed : true;
  // SPA-618. Per-team gate — only teams that opted in can hide invites.
  const canDeleteInvites = outreacher ? outreacher.teamSettings.canDeleteInvites : false;

  const query = useQuery({
    queryKey: ['invites', tab, showOwed, page],
    queryFn: () =>
      api.getPaged<OutreachInvite[]>('/portal/invites', {
        status: tabDef?.status,
        showOwed: showOwed ? 'true' : undefined,
        page,
        limit: PAGE_SIZE,
      }),
    enabled: !isMessaged,
  });

  // Separate query because the row shape differs: these have no invite behind
  // them. Backend scopes to the calling outreacher, so this only ever shows
  // their own outreach, never a teammate's.
  const contactedQuery = useQuery({
    queryKey: ['invites', 'contacted', page],
    queryFn: () =>
      api.getPaged<ContactedNotReplied[]>('/portal/invites', {
        status: 'CONTACTED',
        page,
        limit: PAGE_SIZE,
      }),
    enabled: isMessaged,
  });

  // Messaged rows that are also queue leads get the queue's Log reply (so the
  // lead is marked logged) and its stats. Matched on channel + handle against
  // the caller's messaged leads only, which is a small list.
  const messagedLeads = useQueueLeads('CONTACTED', isMessaged);
  const leadsByHandle = useMemo(() => {
    const m = new Map<string, DiscoveryProspect>();
    for (const p of messagedLeads.leads) {
      if (p.contactedByMe) m.set(handleKey(p.channel, p.targetIdentifier), p);
    }
    return m;
  }, [messagedLeads.leads]);
  const contactedRows = contactedQuery.data?.data ?? [];
  const [loggingLead, setLoggingLead] = useState<DiscoveryProspect | null>(null);

  // Niche codes → names, for the Niche column and the edit dialog.
  const allNiches = useQuery({
    queryKey: ['niches', 'all'],
    // Display lookup: hidden niches still name old invites.
    queryFn: () => api.get<OutreachNiche[]>('/portal/niches', { includeHidden: true }),
    enabled: cols.isVisible('niche'),
    staleTime: 5 * 60_000,
  });
  const nicheName = useMemo(() => {
    const map = new Map((allNiches.data ?? []).map((n) => [n.code, n.displayName]));
    return (code: string): string => map.get(code) ?? titleCase(code);
  }, [allNiches.data]);

  // Inline edit-handle on each row. Same backend route as the detail page:
  // owner-only, refused on rejected and paid invites.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftHandle, setDraftHandle] = useState('');
  const editMutation = useMutation({
    mutationFn: ({ publicId, value }: { publicId: string; value: string }) =>
      api.patch<OutreachInvite>(`/portal/invites/${publicId}`, { targetIdentifier: value }),
    onSuccess: (inv) => {
      toast.success(`Saved as @${inv.targetIdentifier}. ${HANDLE_RECHECK}`);
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      void queryClient.invalidateQueries({ queryKey: ['invite', inv.publicId] });
      setEditingId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function startEdit(inv: OutreachInvite): void {
    setDraftHandle(`@${inv.targetIdentifier}`);
    setEditingId(inv.id);
  }

  const [hidingInvite, setHidingInvite] = useState<OutreachInvite | null>(null);
  const hideMutation = useMutation({
    mutationFn: (publicId: string) =>
      api.del<{ publicId: string; deletedAt: string | null }>(`/portal/invites/${publicId}`),
    onSuccess: (_data, publicId) => {
      const target = hidingInvite?.targetIdentifier ?? publicId;
      toast.success(`Hid @${target}. Admins can still see it.`);
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      setHidingInvite(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Details dialog (niches / notes / email) ──────────────────────────
  const [detailsEditing, setDetailsEditing] = useState<OutreachInvite | null>(null);
  const [detailsNiches, setDetailsNiches] = useState<string[]>([]);
  const [detailsNotes, setDetailsNotes] = useState('');
  const [detailsEmail, setDetailsEmail] = useState('');

  const detailsNichesQuery = useQuery({
    queryKey: ['niches', detailsEditing?.category],
    queryFn: () =>
      api.get<OutreachNiche[]>(
        '/portal/niches',
        detailsEditing?.category ? { category: detailsEditing.category } : undefined,
      ),
    enabled: Boolean(detailsEditing),
  });

  const detailsMutation = useMutation({
    mutationFn: ({
      publicId,
      body,
    }: {
      publicId: string;
      body: { niches?: string[]; notes?: string | null; email?: string | null };
    }) => api.patch<OutreachInvite>(`/portal/invites/${publicId}`, body),
    onSuccess: () => {
      toast.success('Details saved');
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      setDetailsEditing(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function openDetailsEdit(inv: OutreachInvite): void {
    setDetailsNiches([...inv.niches]);
    setDetailsNotes(inv.notes ?? '');
    setDetailsEmail(inv.email ?? '');
    setDetailsEditing(inv);
  }
  function submitDetails(): void {
    if (!detailsEditing) return;
    const original = detailsEditing;
    const nichesChanged =
      detailsNiches.length !== original.niches.length ||
      detailsNiches.some((n, i) => n !== original.niches[i]);
    const notesChanged = (detailsNotes || null) !== (original.notes ?? null);
    const emailChanged = (detailsEmail || null) !== (original.email ?? null);
    if (!nichesChanged && !notesChanged && !emailChanged) {
      toast.info('No changes to save');
      setDetailsEditing(null);
      return;
    }
    const body: { niches?: string[]; notes?: string | null; email?: string | null } = {};
    if (nichesChanged) body.niches = detailsNiches;
    if (notesChanged) body.notes = detailsNotes || null;
    if (emailChanged) body.email = detailsEmail || null;
    detailsMutation.mutate({ publicId: original.publicId, body });
  }
  function submitEdit(inv: OutreachInvite): void {
    if (!HANDLE_EDITABLE_CHANNELS.has(inv.channel)) {
      toast.error(`Handles can't be edited here for ${channelLabel(inv.channel)}. Ask an admin.`);
      return;
    }
    const parsed = parseSocialHandle(inv.channel as SocialChannel, draftHandle);
    if (!parsed) {
      toast.error(INVALID_HANDLE);
      return;
    }
    if (parsed.username === inv.targetIdentifier) {
      setEditingId(null);
      return;
    }
    editMutation.mutate({ publicId: inv.publicId, value: parsed.username });
  }

  const allRows = query.data?.data ?? [];
  const expiredOnPage = allRows.filter((r) => r.status === 'PENDING' && r.releasedAt && !r.deletedAt).length;
  const rows = hideExpired
    ? allRows.filter((r) => !(r.status === 'PENDING' && r.releasedAt && !r.deletedAt))
    : allRows;
  const meta = query.data?.meta ?? { page, limit: PAGE_SIZE, total: allRows.length };

  return (
    <div className="space-y-6">
      <PageHeader
        title="My creators"
        description="Creators you messaged or logged. Tap “I messaged them” when you DM someone, then Log reply once they ask how to join. They move on to Joined and Posted as they sign up and post."
        actions={
          <Button asChild>
            <Link to="/portal/log">Log reply</Link>
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Stage" className="flex flex-wrap items-center gap-2">
          {MY_CREATORS_TABS.map((t) => (
            <Button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              variant={tab === t.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUrl({ tab: t.key, page: 1 })}
            >
              {t.label}
            </Button>
          ))}
        </div>
        {owedFilterAvailable && (
          <Button
            variant={showOwed ? 'default' : 'outline'}
            size="sm"
            aria-pressed={showOwed}
            onClick={() => setUrl({ owed: !showOwed, page: 1 })}
          >
            Still to be paid
          </Button>
        )}
        {expiredToggleAvailable && (
          <Button
            variant={hideExpired ? 'default' : 'outline'}
            size="sm"
            aria-pressed={hideExpired}
            title="Only hides expired invites on the page you're looking at. Other pages and the total still include them."
            onClick={() => setUrl({ hideExpired: !hideExpired })}
          >
            <EyeOff className="h-3.5 w-3.5" />
            Hide expired on this page
          </Button>
        )}
        {!isMessaged && (
          <ColumnPicker columns={INVITER_COLUMNS} visible={cols.visible} onToggle={cols.toggle} />
        )}
      </div>

      {isMessaged ? (
        <>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {contactedQuery.isLoading
              ? 'Loading'
              : `${(contactedQuery.data?.meta?.total ?? 0).toLocaleString()} waiting on a reply`}
          </p>
          <p className="text-sm text-muted-foreground">
            Creators you messaged who haven't replied yet. Nothing is owed for these. They leave this
            list once someone logs their reply, including a teammate who gets there first.
          </p>
          {contactedQuery.isError ? (
            <p className="text-sm text-destructive">Couldn't load your messaged creators. Try again in a moment.</p>
          ) : contactedQuery.isLoading ? (
            <TableSkeleton cols={['w-32', 'w-20', 'w-20', 'w-12', 'w-16']} />
          ) : contactedRows.length === 0 ? (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="Nobody's waiting on a reply."
              hint="Creators you mark as messaged show up here until their reply is logged."
            />
          ) : (
            <>
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info className="mt-px h-3.5 w-3.5 shrink-0" />
                {QUEUE_LEGEND}
              </p>
              <ul className="panel divide-y divide-border overflow-hidden rounded-xl">
                {contactedRows.map((r) => (
                  <MessagedCreatorRow
                    key={r.publicId}
                    row={r}
                    prospect={leadsByHandle.get(handleKey(r.channel, r.targetIdentifier))}
                    onLogReply={setLoggingLead}
                  />
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        <>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {query.isLoading
              ? 'Loading'
              : `${meta.total.toLocaleString()} invite${meta.total === 1 ? '' : 's'}`}
            {hideExpired && expiredOnPage > 0 && (
              <span className="ml-2 normal-case tracking-normal">
                · {expiredOnPage} expired hidden on this page only
              </span>
            )}
          </p>

          {query.isError ? (
            <p className="text-sm text-destructive">Couldn't load your creators. Try again in a moment.</p>
          ) : query.isLoading ? (
            <TableSkeleton cols={['w-32', 'w-16', 'w-20', 'w-16', 'w-16']} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Inbox className="h-6 w-6" />}
              title={allRows.length > 0 ? 'Everything on this page has expired.' : 'No creators here.'}
              hint={
                allRows.length > 0
                  ? 'Turn off "Hide expired on this page" or go to the next page.'
                  : showOwed
                    ? 'Nothing is still to be paid here. Turn off "Still to be paid" to see everything.'
                    : (EMPTY_HINT[tab] ?? EMPTY_HINT.all)
              }
            />
          ) : (
            <DataTable>
              <DataTableHeader>
                <tr>
                  <DataTableHeaderCell>Creator</DataTableHeaderCell>
                  {cols.isVisible('source') && <DataTableHeaderCell>Source</DataTableHeaderCell>}
                  {cols.isVisible('channel') && <DataTableHeaderCell>Channel</DataTableHeaderCell>}
                  {cols.isVisible('date') && <DataTableHeaderCell>Logged</DataTableHeaderCell>}
                  {cols.isVisible('category') && <DataTableHeaderCell>Creator type</DataTableHeaderCell>}
                  {cols.isVisible('niche') && <DataTableHeaderCell>Niche</DataTableHeaderCell>}
                  {cols.isVisible('status') && <DataTableHeaderCell>Stage</DataTableHeaderCell>}
                  {cols.isVisible('age') && <DataTableHeaderCell>Time in stage</DataTableHeaderCell>}
                  {cols.isVisible('followers') && <DataTableHeaderCell numeric>Followers</DataTableHeaderCell>}
                  {cols.isVisible('posts') && <DataTableHeaderCell numeric>Posts</DataTableHeaderCell>}
                  {cols.isVisible('owed') && <DataTableHeaderCell numeric>Still to be paid</DataTableHeaderCell>}
                  <DataTableHeaderCell className="text-right" />
                </tr>
              </DataTableHeader>
              <tbody>
                {rows.map((inv) => {
                  const isEditing = editingId === inv.id;
                  const isHidden = inv.deletedAt !== null;
                  const isRejected = inv.status === 'REJECTED';
                  const alreadyPaid = Number.parseFloat(inv.amountPaid ?? '0') > 0;
                  const unpaidAndOpen = !isHidden && !isRejected && !alreadyPaid;
                  const handleEditable = unpaidAndOpen && HANDLE_EDITABLE_CHANNELS.has(inv.channel);
                  const detailsEditable = unpaidAndOpen;
                  const lockedReason = isHidden
                    ? 'Hidden invites can’t be edited'
                    : isRejected
                      ? 'Not paid invites can’t be edited'
                      : alreadyPaid
                        ? 'Already paid. Ask an admin to change it.'
                        : null;
                  const age = stageAge(inv, { teamTracks });
                  return (
                    <DataTableRow key={inv.id}>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <div className="space-y-1">
                            <Input
                              autoFocus
                              aria-label="New handle"
                              value={draftHandle}
                              onChange={(e) => setDraftHandle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  submitEdit(inv);
                                } else if (e.key === 'Escape') {
                                  setEditingId(null);
                                }
                              }}
                              className="h-7 w-56"
                            />
                            <p className="max-w-[16rem] text-[11px] text-muted-foreground">{HANDLE_RECHECK}</p>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            <PlatformIcon platform={inv.channel} size={14} className="text-muted-foreground" />
                            <Link
                              to={`/portal/invites/${inv.publicId}`}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              @{inv.targetIdentifier}
                            </Link>
                          </span>
                        )}
                      </td>
                      {cols.isVisible('source') && (
                        <td className="px-3 py-2 text-muted-foreground" data-label="Source">
                          {inviteSourceLabel(inv.discoveryFolderName)}
                        </td>
                      )}
                      {cols.isVisible('channel') && (
                        <td className="px-3 py-2" data-label="Channel">
                          <Tag tone={channelToneFor(inv.channel)}>{channelLabel(inv.channel)}</Tag>
                        </td>
                      )}
                      {cols.isVisible('date') && (
                        <td className="whitespace-nowrap px-3 py-2 text-muted-foreground" data-label="Logged">
                          {formatDate(inv.createdAt)}
                        </td>
                      )}
                      {cols.isVisible('category') && (
                        <td className="px-3 py-2" data-label="Creator type">
                          {creatorTypeLabel(inv.category)}
                        </td>
                      )}
                      {cols.isVisible('niche') && (
                        <td className="px-3 py-2 text-muted-foreground" data-label="Niche">
                          {inv.niches.length ? inv.niches.map(nicheName).join(', ') : '—'}
                        </td>
                      )}
                      {cols.isVisible('status') && (
                        <td className="px-3 py-2" data-label="Stage">
                          <InviteStagePill invite={inv} teamTracks={teamTracks} />
                        </td>
                      )}
                      {cols.isVisible('age') && (
                        <td className="whitespace-nowrap px-3 py-2 text-muted-foreground" data-label="Time in stage">
                          {age.text || '—'}
                        </td>
                      )}
                      {cols.isVisible('followers') && (
                        <td className="px-3 py-2 text-right text-muted-foreground" data-label="Followers">
                          {formatFollowers(inv.followerCount)}
                        </td>
                      )}
                      {cols.isVisible('posts') && (
                        <td className="px-3 py-2 text-right text-muted-foreground" data-label="Posts">
                          {inv.postCount > 0 ? inv.postCount : inv.hasPosted === false ? '0' : '—'}
                        </td>
                      )}
                      {cols.isVisible('owed') && (
                        <td
                          className="px-3 py-2 text-right tabular-nums"
                          data-label="Still to be paid"
                          title={isRejected ? 'Not paid: an admin turned this invite down, so nothing is owed on it.' : undefined}
                        >
                          ${formatMoney(stillToBePaid(inv))}
                        </td>
                      )}
                      <td className="actions px-3 py-2 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Save"
                              aria-label="Save handle"
                              disabled={editMutation.isPending || !draftHandle.trim()}
                              onClick={() => submitEdit(inv)}
                            >
                              {editMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Cancel (Esc)"
                              aria-label="Cancel"
                              onClick={() => setEditingId(null)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              title={
                                handleEditable
                                  ? `Edit handle (typo or wrong account). ${HANDLE_RECHECK}`
                                  : (lockedReason ??
                                    `Handles can't be edited here for ${channelLabel(inv.channel)}. Ask an admin.`)
                              }
                              aria-label="Edit handle"
                              disabled={!handleEditable}
                              onClick={() => startEdit(inv)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              title={detailsEditable ? 'Edit niches, notes or email' : (lockedReason ?? '')}
                              aria-label="Edit niches, notes or email"
                              disabled={!detailsEditable}
                              onClick={() => openDetailsEdit(inv)}
                            >
                              <Tags className="h-4 w-4" />
                            </Button>
                            <ScreenshotViewer
                              scope="portal"
                              publicId={inv.publicId}
                              targetIdentifier={inv.targetIdentifier}
                              hasScreenshot={!!inv.screenshotKey}
                            />
                            {/* SPA-618. Team-gated soft-delete, shown as Hide. */}
                            {canDeleteInvites && !isHidden && (
                              <Button
                                size="icon"
                                variant="ghost"
                                title="Hide from your list (admins still see it)"
                                aria-label="Hide"
                                onClick={() => setHidingInvite(inv)}
                              >
                                <EyeOff className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </DataTableRow>
                  );
                })}
              </tbody>
            </DataTable>
          )}
        </>
      )}

      <Pagination
        page={isMessaged ? (contactedQuery.data?.meta?.page ?? 1) : meta.page}
        limit={isMessaged ? (contactedQuery.data?.meta?.limit ?? PAGE_SIZE) : meta.limit}
        total={isMessaged ? (contactedQuery.data?.meta?.total ?? 0) : meta.total}
        onPageChange={(p) => setUrl({ page: p })}
      />

      {loggingLead && <LogReplyDialog prospect={loggingLead} onClose={() => setLoggingLead(null)} />}

      <Dialog open={!!detailsEditing} onOpenChange={(open) => !open && setDetailsEditing(null)}>
        <DialogContent
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter submits; Enter alone adds a line in the notes box.
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && detailsEditing) {
              e.preventDefault();
              submitDetails();
            }
          }}
        >
          {detailsEditing && (
            <>
              <DialogHeader>
                <DialogTitle>Edit @{detailsEditing.targetIdentifier}</DialogTitle>
                <DialogDescription>
                  Change the niches, notes or email on this invite. To fix the handle itself, use the
                  pencil on the row.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3">
                <div className="grid gap-1">
                  <Label>Niches</Label>
                  <NichesPicker
                    niches={sortNichesByName(detailsNichesQuery.data ?? [])}
                    nameOf={nicheName}
                    value={detailsNiches}
                    onChange={setDetailsNiches}
                    multiple={multipleNichesAllowed}
                    loading={detailsNichesQuery.isLoading}
                  />
                  {!multipleNichesAllowed && (
                    <p className="text-xs text-muted-foreground">Your team allows one niche per invite.</p>
                  )}
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="detailsNotes">Notes</Label>
                  <textarea
                    id="detailsNotes"
                    value={detailsNotes}
                    onChange={(e) => setDetailsNotes(e.target.value)}
                    rows={3}
                    placeholder="Anything an admin should know about this creator"
                    className="rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="detailsEmail">Email (optional)</Label>
                  <Input
                    id="detailsEmail"
                    type="email"
                    value={detailsEmail}
                    onChange={(e) => setDetailsEmail(e.target.value)}
                    placeholder="creator@example.com"
                    autoComplete="off"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDetailsEditing(null)}>
                  Cancel
                </Button>
                <Button type="button" onClick={submitDetails} disabled={detailsMutation.isPending}>
                  {detailsMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!hidingInvite} onOpenChange={(open) => !open && setHidingInvite(null)}>
        <DialogContent>
          {hidingInvite && (
            <>
              <DialogHeader>
                <DialogTitle>Hide @{hidingInvite.targetIdentifier}?</DialogTitle>
                <DialogDescription>
                  It stays in your list with a Hidden label and you can't edit it any more. Admins still
                  see it. Any amount it earned stays until an admin writes it off.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setHidingInvite(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={hideMutation.isPending}
                  onClick={() => hideMutation.mutate(hidingInvite.publicId)}
                >
                  {hideMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Hide
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

type OutreacherMe = Extract<Me, { role: 'outreacher' }>;
function asOutreacher(me: Me | undefined): OutreacherMe | null {
  return me && me.role === 'outreacher' ? me : null;
}

function sortNichesByName(list: OutreachNiche[]): OutreachNiche[] {
  return [...list].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function formatFollowers(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}
